// Portfolio-level fan-out: fetches Adresses + addressVisit Tasks across all
// active child projects that have the housevisits module enabled. One-shot
// .get() per collection in batches of 10 (Firestore `in` limit).
//
// The Tasks query filters server-side on Type==='addressVisit' to avoid
// pulling the entire Tasks collection per project. This requires a composite
// index (CompagnyID + Type) — see firestore.indexes.json.
//
// AI-CONTEXT: See docs/patterns.md#dashboard-portfolio-aggregation-card

import { useEffect, useMemo, useState } from 'react'
import { db } from '../../firebase/config'
import { client } from '../Client'
import { useFirestoreAllArrayContains } from '../../firebase/useFirestore'
import { selectHouseVisitsProjects } from '../../helpers/housevisitsPortfolio/aggregateHouseVisits'

const BATCH_SIZE = 10

const chunk = (arr, size) => {
    const out = []
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size))
    return out
}

/**
 * @typedef {object} EqualityFilter
 * @property {string} field
 * @property {string|boolean|number} value
 */

/**
 * @param {string} collection
 * @param {Array<string>} ids
 * @param {EqualityFilter} [extraFilter]
 */
const fetchByCompagnyIdIn = async (collection, ids, extraFilter) => {
    const batches = chunk(ids, BATCH_SIZE)
    const results = await Promise.all(
        batches.map((batch) => {
            let q = db.collection(collection).where('CompagnyID', 'in', batch)
            if (extraFilter) {
                q = q.where(extraFilter.field, '==', extraFilter.value)
            }
            return q
                .get()
                .then((snap) => snap.docs.map((d) => ({ ...d.data(), docid: d.id })))
                .catch((err) => {
                    console.error(
                        `useHouseVisitsPortfolioData: ${collection} fetch failed`,
                        err
                    )
                    return []
                })
        })
    )
    return results.flat()
}

/**
 * @typedef {object} HouseVisitsPortfolioData
 * @property {Array<{CompagnyID: string, CommunityName?: string}>} projects - Active child projects with HouseVisits=true
 * @property {Array<object>} adresses - All Adresses across those projects
 * @property {Array<object>} tasks - All addressVisit Tasks across those projects
 * @property {boolean} loading
 */

/**
 * @param {boolean} showDemoProjects - CompagnyMeta.ShowDemoProjects van de portfolio
 *   (doorgegeven i.p.v. useSettings hier: dat opent drie extra listeners)
 * @returns {HouseVisitsPortfolioData}
 */
const useHouseVisitsPortfolioData = (showDemoProjects) => {
    const childProjects = useFirestoreAllArrayContains('CompagnyMeta', 'Parent', client)

    const activeHouseVisitsProjects = useMemo(
        () => selectHouseVisitsProjects(childProjects, showDemoProjects),
        [childProjects, showDemoProjects]
    )

    const projectIds = useMemo(
        () => activeHouseVisitsProjects.map((p) => p.CompagnyID).filter(Boolean),
        [activeHouseVisitsProjects]
    )
    const idsKey = useMemo(() => [...projectIds].sort().join(','), [projectIds])

    const [adresses, setAdresses] = useState(/** @type {Array<object>} */ ([]))
    const [tasks, setTasks] = useState(/** @type {Array<object>} */ ([]))
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        let cancelled = false

        const run = async () => {
            if (projectIds.length === 0) {
                setAdresses([])
                setTasks([])
                setLoading(false)
                return
            }

            setLoading(true)
            const [adressDocs, taskDocs] = await Promise.all([
                fetchByCompagnyIdIn('Adresses', projectIds),
                fetchByCompagnyIdIn('Tasks', projectIds, {
                    field: 'Type',
                    value: 'addressVisit',
                }),
            ])
            if (cancelled) return
            setAdresses(adressDocs)
            setTasks(taskDocs)
            setLoading(false)
        }

        run()
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idsKey])

    return {
        projects: activeHouseVisitsProjects,
        adresses,
        tasks,
        loading,
    }
}

export default useHouseVisitsPortfolioData
